import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { paintings } from '../data/paintings';

const Home = () => {
  const { t, lang } = useLanguage();
  const featuredArtworks = paintings.slice(0, 3);

  return (
    <div className="min-h-screen bg-gallery-bg">
      {/* Hero Section */}
      <section className="relative h-screen w-full flex items-center justify-center overflow-hidden" aria-label="Hero">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/6f319949afba45b9008649532004af25bb95492e2b2fb555f5bdcdad5193a9e70a9e6d92944cfc73f8b57561372ade93/images/aW1hZ2UvanBlZw==/a387ab8ccb91839155e3e4a7021918cd.jpeg" 
            alt="Detalle de pintura al óleo contemporánea por Gisselle Mendez" 
            className="w-full h-full object-cover opacity-90 scale-125 transform" 
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <motion.h1 
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1 }}
            className="text-5xl md:text-7xl lg:text-8xl font-serif text-white mb-6"
          >
            {t('hero.title')}
          </motion.h1>
          <motion.p 
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="text-lg md:text-xl text-white/90 font-light mb-10 tracking-wide"
          >
            {t('hero.subtitle')}
          </motion.p>
          <motion.div 
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1, delay: 0.6 }}
          >
            <Link 
              to="/gallery" 
              className="inline-block border border-white/50 text-white px-8 py-4 uppercase tracking-widest text-sm hover:bg-white hover:text-gallery-text transition-all duration-500"
            >
              {t('hero.cta')}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Section */}
      <section className="py-32 px-6 max-w-7xl mx-auto" aria-labelledby="featured-heading">
        <div className="text-center mb-20">
          <h2 id="featured-heading" className="text-4xl font-serif mb-4">Colección Destacada</h2>
          <div className="w-16 h-px bg-gallery-accent mx-auto"></div>
          <p className="mt-4 text-gallery-muted max-w-xl mx-auto italic">
            Selección exclusiva de obras originales disponibles para adquisición.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {featuredArtworks.map((artwork, index) => (
            <motion.article 
              key={artwork.id}
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="group cursor-pointer"
            >
              <Link to="/gallery">
                <div className="relative overflow-hidden aspect-[3/4] mb-6 shadow-lg bg-gallery-surface">
                  <img 
                    src={artwork.image} 
                    alt={`${artwork.title} - Pintura de Gisselle Mendez`} 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                    loading="lazy"
                  />
                  {!artwork.available && (
                    <div className="absolute top-4 right-4 bg-black/70 text-white text-xs uppercase tracking-widest px-3 py-1">
                      {t('artwork.soldOut')}
                    </div>
                  )}
                </div>
                <h3 className="font-serif text-xl mb-1">
                  {lang === 'en' ? artwork.titleEn || artwork.title : artwork.title}
                </h3>
                <p className="text-sm text-gallery-muted">
                  {artwork.available ? `€${artwork.price.toLocaleString()}` : t('artwork.soldOut')}
                </p>
              </Link>
            </motion.article>
          ))}
        </div>

        <div className="text-center mt-20">
          <Link 
            to="/gallery" 
            className="text-gallery-text hover:text-gallery-accent uppercase tracking-widest text-sm border-b border-gallery-text hover:border-gallery-accent pb-1 transition-colors"
          >
            Ver Todas las Obras
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;